/* 
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {hasAccountIds} from '@ion/core/src/entities/Accounts';
import {translate} from '@ion/core/src/locale/locale';
import {loadLandmarkGeoFences, searchGeoFences} from '@ion/core/src/services/ArcGIS';
import {FilterOption, FilterPropsType} from '../types/types';
import Filter from './Filter';
import {getMenuStyles} from './FilterHelpers';
import {FilterParamsProps} from './useFilterSubmit';

/**
 * Defines the prop definitions for GeofenceFilter ...
 * 
 * account:        The current account selected in global chooser.
 * accontIds:      The list of account ids under current account.
 * loadGeofences:  Override function to load the default option data.
 * actionLabel:    The label of the action button.
 * onActionClick:  The onClick event of action button.
 */
export interface GeofenceFilterProps extends FilterPropsType {
  account: number;
  accountIds: number[];
  loadGeofences?: Function;
  actionLabel?: string;
  onActionClick?: Function;
  ancestorAccountIds?: number[];
  descendantAccountId?: any;
  filterRefresh?: boolean;
};

/**
 * The GeofenceFilter component is a reusable filter for managing the filtering
 * of geofence. It is intended to be used in mutiple pages and be a responsive.
 * 
 * ```typescript
 * 
 * import GeofenceFilter from '@ion/dls/src/filters';
 * 
 * <GeofenceFilter elementId={elementId}
 *   name={name}
 *   account={account}
 *   accountIds={accountIds}
 *   loadGeofences={loadGeofences}
 *   filterState={filterState}
 *   showSelectedOptionsCount={false}
 *   actionLabel={actionLabel}
 *   onActionClick={onActionClick}
 *   primaryButtonDisable={primaryButtonDisabled}
 *   secondaryButtonDisable={secondaryButtonDisabled}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 *   onMenuClose={onMenuClose} />
 * 
 * ```
 */
export const GeofenceFilter = ({
  elementId,
  name,
  account,
  accountIds,
  loadGeofences,
  actionLabel,
  onActionClick,
  primaryButtonDisabled,
  secondaryButtonDisabled,
  onSubmit,
  onCancel,
  onMenuClose,
  ancestorAccountIds,
  filterState = {},
  showSelectedOptionsCount = true,
  isMobileView = false,
  descendantAccountId,
  filterRefresh
}: GeofenceFilterProps) => {
  const [refresh, setRefresh] = useState<boolean>(false);
  const {
    loading: isLoading,
    error: hasErrors,
    selected: selectedOptions
  } = filterState;
  const {
    menuLeft,
    menuWidth,
    contentGridTemplateColumns,
    contentJustifyContent,
    contentGridColumnGap,
    contentGridRowGap
  } = getMenuStyles(isMobileView);

  function handleSubmit({selections, options, loading, hasError}) {
    const filterParams: FilterParamsProps = {
      optionsKey        : FilterOption.landmarkName,
      selectionsKey     : FilterOption.value,
      augmentSelections : selected => ({
        id   : selected[FilterOption.objectId],
        name : selected[FilterOption.landmarkName]
      }),
      options,
      selections,
      loading,
      hasError
    };
    onSubmit?.(name, filterParams);
  };

  useEffect(() => {
    setRefresh(true);
  }, [descendantAccountId, ancestorAccountIds]);

  useEffect(function() {
    setRefresh(filterRefresh);
  }, [filterRefresh]);

  const loadFilterOptions = useCallback(async function(accountIds) {
    const query = loadLandmarkGeoFences(ancestorAccountIds, descendantAccountId);
    let geofences = [];
    if (loadGeofences) {
      geofences = await loadGeofences();
    } else if (hasAccountIds(descendantAccountId)) {
      // skips the request when there are no account ids to query (e.g. the global group is still loading)...
      geofences = await searchGeoFences({query});
    }

    return {
      result       : geofences,
      filterResult : geofences
    };
  }, [accountIds, ancestorAccountIds, loadGeofences, account, descendantAccountId]);

  const updateRefresh = useCallback(function() {
    setRefresh(!refresh);
  }, [refresh]);

  const selectedOptionIds = useMemo(() => {
    return selectedOptions?.map(option => option?.id ?? option) || [];
  }, [selectedOptions]);

  return (
    <Filter optionType={translate('common:filters.geofences')}
      selectedAccount={account}
      optionPropId={FilterOption.objectId}
      optionPropLabel={FilterOption.landmarkName}
      selectedOptionIds={selectedOptionIds}
      loading={isLoading}
      showSelectedOptionsCount={showSelectedOptionsCount}
      actionLabel={actionLabel}
      onActionClick={onActionClick}
      primaryButtonDisabled={primaryButtonDisabled}
      secondaryButtonDisabled={secondaryButtonDisabled}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      onMenuClose={onMenuClose}
      elementId={elementId}
      menuLeft={menuLeft}
      menuWidth={menuWidth}
      contentGridTemplateColumns={contentGridTemplateColumns}
      contentJustifyContent={contentJustifyContent}
      contentGridColumnGap={contentGridColumnGap}
      contentGridRowGap={contentGridRowGap}
      loadFilterOptions={loadFilterOptions}
      accountId={accountIds}
      isMobileView={isMobileView}
      refresh={refresh}
      updateRefresh={updateRefresh}/>
  );
};

export default GeofenceFilter;
